import { useCallback, useEffect, useState } from 'react';
import {
  ALERT_SEVERITIES,
  alertFindingRowSchema,
  alertIsActive,
  decodeFilterItem,
  encodeFilterItem,
  filterItems,
  type AlertFindingRow,
  type Project,
} from '@forecastify/shared';
import {
  DataList,
  type DataListColumn,
  type DataListRow,
  type MenuItem,
  type SortState,
  type Toast,
  Toaster,
} from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { ALERT_SCREEN_TOPICS } from '../../live/topics';
import { useLiveInvalidation } from '../../live/use-live';
import { useSession } from '../../session';
import { useWorkspaceNavigate } from '../../workspace-path';
import { sortInMemory } from '../sort-in-memory';
import { useRowPages } from '../use-row-pages';
import { parseList, usePermissionSet } from '../permissions-hook';
import { listLabels } from '../leave/requests-screen';
import { AlertDetailDialog } from './alert-detail-dialog';
import { DismissAlertDialog } from './dismiss-alert-dialog';
import {
  alertName,
  alertTarget,
  areaLabel,
  goLabel,
  penaltyOf,
  severityLabel,
  severityOf,
  SEVERITY_TONE,
} from './alert-text';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A projekt Alertek fülje — C59. The 10th tab.                              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `phase5/03-project-alerts.png`. Seven columns, three views, two filters, a search and a footer
 * that counts — `DataList`, like every other table in this product.
 *
 * ⚠️⚠️ **`ARCHIVÁLT` IS BOTH RESOLVED AND WAIVED, AND THE MOCKUP ONLY HAD THE FIRST.** Its findings
 * carried one flag; P5-5 split the dismissal off into a standing exception, which leaves a finding
 * that is open **and** suppressed. `alertIsActive` in `@shared` is where that split is decided and
 * why — the short version is that the views sort on *what is counting against RISK right now*,
 * while the ÁLLAPOT column still distinguishes *fixed* from *waived*, because those are different
 * things and a reader who cannot tell them apart can audit neither.
 *
 * ⚠️⚠️ **THERE IS NO REFRESH BUTTON, AND ITS ABSENCE IS P5-4.** The read reconciles when the
 * project is stale, so the list is current by the time it arrives. A button would be a second way
 * to reach one thing, and the first person to press it and see nothing change would conclude it
 * was broken.
 *
 * ⚠️ **Filtering in memory, deliberately, and CLAUDE.md §3 allows it here.** The rule is about
 * lists the server pages; a project's findings are one unpaged response of a few dozen rows — the
 * endpoint returns the project's whole set because RISK is computed from all of it.
 */

type View = 'active' | 'archived' | 'all';

export function ProjectAlertsTab({
  project,
  onGoToTab,
}: {
  readonly project: Project;
  /**
   * ⚠️ **The kebab's jump for the four subjects with no address of their own** — see
   * `alert-text.ts`. The tab cannot switch the project's tab by itself, so the screen that owns
   * that state passes the setter down.
   */
  readonly onGoToTab: (tab: string) => void;
}) {
  const t = useT();
  const go = useWorkspaceNavigate();
  const session = useSession();
  const permissions = usePermissionSet();

  const [findings, setFindings] = useState<readonly AlertFindingRow[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [view, setView] = useState<View>('active');
  const [query, setQuery] = useState('');
  const [severity, setSeverity] = useState('');
  const [sort, setSort] = useState<readonly SortState[]>([]);
  const [area, setArea] = useState('');
  const [opened, setOpened] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<string | null>(null);
  /* 30 Oct 2026 · the dialog was opened from a group row — it waives every active one of the kind. */
  const [dismissingAll, setDismissingAll] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [serverError, setServerError] = useState<string | null>(null);
  /* 26 Oct 2026 · a waive in flight keeps its dialog open; its outcome is a toast. */
  const [waiving, setWaiving] = useState(false);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const toast = (title: string): void => {
    setToasts((current) => [
      ...current,
      { id: `alert-${String(Date.now())}`, title, variant: 'success' },
    ]);
  };

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) return;
    const response = await session.request(`/projects/${project.id}/alerts`).catch(() => null);
    setLoading(false);
    if (response?.ok !== true) return;
    setFindings(parseList(alertFindingRowSchema, await response.json()) ?? []);
  }, [session, project.id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * ⚠️⚠️ **R-19: THE ROUTE DECLARES THE TOPICS AND THIS SUBSCRIBES TO THEM.** Fourteen of them,
   * because a finding is a statement about almost every table — and the reconcile itself rides the
   * same list on the server (P5-4). A tab that read once on mount would show the alert somebody
   * just fixed, which is the single worst thing this screen could do.
   */
  useLiveInvalidation(ALERT_SCREEN_TOPICS, () => {
    void reload();
  });

  /**
   * 26 Oct 2026 · ⚠️ **THE RESPONSE IS THE NEW LIST, SO IT IS USED.** Waive and restore both answer
   * with the project's findings from the same transaction; this re-read them instead, then the
   * live topic re-read them again — the row sat unchanged in between, which read as nothing having
   * happened. The waived count, when there is one, comes back in `meta`.
   */
  const write = async (
    path: string,
    init: RequestInit,
  ): Promise<{ readonly ok: boolean; readonly waived: number | null }> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response?.ok !== true) {
      setServerError(
        response?.status === 404 ? t('alerts.error.denied') : t('alerts.error.failed'),
      );
      return { ok: false, waived: null };
    }
    const body = (await response.json().catch(() => null)) as {
      meta?: { waived?: unknown };
    } | null;
    const next = parseList(alertFindingRowSchema, body);
    if (next === null) await reload();
    else setFindings(next);
    const waived = body?.meta?.waived;
    return { ok: true, waived: typeof waived === 'number' ? waived : null };
  };

  const canManage = permissions.has('settings.manage');

  /* ── the three views, and what each one holds ───────────────────────────────────────────── */
  const active = findings.filter((one) => alertIsActive(one));
  const archived = findings.filter((one) => !alertIsActive(one));
  const inView = view === 'active' ? active : view === 'archived' ? archived : [...findings];

  const needle = query.trim().toLowerCase();
  const rowsIn = inView.filter((one) => {
    /* 3 Nov 2026 · each filter is several values — OR within, AND across. */
    if (severity !== '' && !filterItems(severity).includes(severityOf(one.kind))) return false;
    if (area !== '' && !filterItems(area).map(decodeFilterItem).includes(areaLabel(t, one.kind))) {
      return false;
    }
    if (needle === '') return true;
    return `${alertName(t, one.kind)} ${one.subjectLabel} ${areaLabel(t, one.kind)}`
      .toLowerCase()
      .includes(needle);
  });

  const columns: DataListColumn[] = [
    { key: 'severity', label: t('alerts.column.severity'), width: 116, sortKey: 'severity' },
    { key: 'name', label: t('alerts.column.name'), sortKey: 'name' },
    { key: 'subject', label: t('alerts.column.subject'), width: 240, sortKey: 'subject' },
    { key: 'area', label: t('alerts.column.area'), width: 124, sortKey: 'area' },
    {
      key: 'penalty',
      label: t('alerts.column.penalty'),
      width: 88,
      align: 'right',
      sortKey: 'penalty',
    },
    { key: 'detected', label: t('alerts.column.detected'), width: 96, sortKey: 'detected' },
    { key: 'state', label: t('alerts.column.state'), width: 104, sortKey: 'state' },
  ];

  /**
   * ⚠️⚠️ **SORTED BEFORE THE ROWS ARE BUILT, NOT AFTER.** The cells carry formatted text —
   * *Magas*, *−8*, *2026-09-21* — and sorting those would order severity alphabetically and the
   * penalty as a string. The reader below takes the FINDING's own fields, so *Magas* sorts above
   * *Közepes* because the severity rank says so rather than because M precedes K.
   *
   * ⚠️ In memory, and that is right here: the tab holds every finding for the project, so there
   * is no page for a sort to lie about.
   */
  const sorted = sortInMemory(rowsIn, sort, {
    /* ⚠️ The declared order — high, mid, low — not the label's alphabet. */
    severity: (one) => ALERT_SEVERITIES.indexOf(severityOf(one.kind)),
    name: (one) => alertName(t, one.kind),
    subject: (one) => one.subjectLabel,
    area: (one) => areaLabel(t, one.kind),
    penalty: (one) => (alertIsActive(one) ? penaltyOf(one.kind) : 0),
    detected: (one) => one.firstDetectedAt,
    state: (one) => (one.exception !== null ? 2 : alertIsActive(one) ? 0 : 1),
  });

  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ 30 Oct 2026 · ONE ROW PER KIND, OPENED TO ITS FINDINGS.            │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * A rule that fires per worklog fired a hundred times on one project and the tab became that
   * rule (UAT). Findings of one kind fold into a row with a counter; a kind with only one finding
   * stays a plain row, since a group of one is a click for nothing.
   *
   * ⚠️ **Grouped AFTER the sort and the filters**, so a group sits where its first member would,
   * its members keep the sort, and the counter is what the filters left.
   *
   * ⚠️ **Paged over what is SHOWN — the open groups' members included.** Paging by group put all
   * 1 351 of one kind on a single page once it was opened (Haystack). A page that starts inside
   * a group repeats that group's row on top, so the reader still sees whose members these are.
   * Rows are built for the page only; an open kind of thousands is not drawn whole.
   */
  const groups: (readonly AlertFindingRow[])[] = [];
  const byKind = new Map<string, AlertFindingRow[]>();
  for (const one of sorted) {
    const members = byKind.get(one.kind);
    if (members === undefined) {
      const fresh = [one];
      byKind.set(one.kind, fresh);
      groups.push(fresh);
    } else {
      members.push(one);
    }
  }

  type Shown =
    | { readonly kind: 'group'; readonly members: readonly AlertFindingRow[] }
    | {
        readonly kind: 'finding';
        readonly finding: AlertFindingRow;
        readonly parent: readonly AlertFindingRow[] | null;
      };
  const groupIdOf = (kind: string): string => `kind:${kind}`;
  const shown: Shown[] = groups.flatMap((members): Shown[] => {
    const first = members[0];
    if (first === undefined) return [];
    if (members.length === 1) return [{ kind: 'finding', finding: first, parent: null }];
    return [
      { kind: 'group', members },
      ...(expanded.has(groupIdOf(first.kind))
        ? members.map((finding): Shown => ({ kind: 'finding', finding, parent: members }))
        : []),
    ];
  });

  /* ⚠️ The settled pager, over what the search and filters left — `use-row-pages.ts`. */
  const paged = useRowPages(shown, JSON.stringify([query, severity, area, view, sort]));
  const groupRow = (members: readonly AlertFindingRow[]): DataListRow => {
    const id = groupIdOf(members[0]?.kind ?? '');
    return groupRowOf(t, id, members, expanded.has(id));
  };
  const head = paged.rows[0];
  const rows: DataListRow[] = [
    ...(head?.kind === 'finding' && head.parent !== null ? [groupRow(head.parent)] : []),
    ...paged.rows.map((one) =>
      one.kind === 'group'
        ? groupRow(one.members)
        : { ...rowOf(t, one.finding), nested: one.parent !== null },
    ),
  ];
  const membersOf = (id: string): readonly AlertFindingRow[] =>
    byKind.get(id.startsWith('kind:') ? id.slice(5) : '') ?? [];

  const clearAll = () => {
    setQuery('');
    setSeverity('');
    setArea('');
    setView('active');
  };

  const jump = (finding: AlertFindingRow): void => {
    const target = alertTarget(finding);
    setOpened(null);
    if (target.kind === 'route') go(target.to);
    else onGoToTab(target.tab);
  };

  const openedFinding = findings.find((one) => one.id === opened) ?? null;
  const dismissingFinding = findings.find((one) => one.id === dismissing) ?? null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {serverError === null ? null : (
        <p className="text-destructive px-4 pt-2 text-[12px]">{serverError}</p>
      )}

      <DataList
        columnsKey="project-alerts"
        card={{
          title: 'name',
          context: 'subject',
          defaults: { badge: 'severity', details: ['area', 'penalty', 'detected', 'state'] },
        }}
        columns={columns}
        sort={sort}
        onSortChange={setSort}
        rows={rows}
        page={paged.page}
        labels={{ ...listLabels(t), table: t('alerts.list') }}
        isLoading={isLoading}
        search={query}
        onSearchChange={setQuery}
        selectable={false}
        view={view}
        onViewChange={(next) => {
          setView(next as View);
        }}
        views={[
          { value: 'active', label: t('alerts.view.active'), count: active.length },
          { value: 'archived', label: t('alerts.view.archived'), count: archived.length },
          { value: 'all', label: t('alerts.view.all'), count: findings.length },
        ]}
        filters={[
          {
            key: 'severity',
            label: t('alerts.filter.severity'),
            kind: 'multi' as const,
            value: severity,
            onChange: setSeverity,
            options: ALERT_SEVERITIES.map((one) => ({
              value: one,
              label: severityLabel(t, one),
            })),
          },
          {
            /* ⚠️ The areas PRESENT, not all twelve — the mockup's own `areas` is derived from the
               list, and offering a filter that can only empty the table is a dead control. */
            key: 'area',
            label: t('alerts.filter.area'),
            kind: 'multi' as const,
            value: area,
            onChange: setArea,
            /* The area's words travel as the value — escaped, since a comma joins the choice. */
            options: [...new Set(findings.map((one) => areaLabel(t, one.kind)))]
              .sort((a, b) => a.localeCompare(b))
              .map((one) => ({ value: encodeFilterItem(one), label: one })),
          },
        ]}
        empty={
          view === 'archived'
            ? {
                icon: 'circle-check',
                title: t('alerts.empty.archived.title'),
                body: t('alerts.empty.archived.body'),
                actionLabel: t('alerts.empty.archived.action'),
                onAction: clearAll,
              }
            : {
                icon: 'circle-check',
                title: t('alerts.empty.active.title'),
                body: t('alerts.empty.active.body'),
                actionLabel: t('alerts.empty.active.action'),
                onAction: clearAll,
              }
        }
        onRowClick={(id) => {
          if (id.startsWith('kind:')) {
            setExpanded((current) => {
              const next = new Set(current);
              if (!next.delete(id)) next.add(id);
              return next;
            });
            return;
          }
          setOpened((current) => (current === id ? null : id));
        }}
        rowMenu={(id) => {
          /* 30 Oct 2026 · a group row only waives — open and the jump belong to one finding. */
          if (id.startsWith('kind:')) {
            const target = membersOf(id).find((one) => alertIsActive(one));
            if (!canManage || target === undefined) return [];
            return [
              {
                label: t('alerts.action.dismissAll'),
                icon: 'square-dashed',
                onSelect: () => {
                  setDismissingAll(true);
                  setDismissing(target.id);
                },
              },
            ];
          }
          const finding = findings.find((one) => one.id === id);
          if (finding === undefined) return [];

          const items: MenuItem[] = [
            {
              label: t('alerts.action.open'),
              icon: 'maximize',
              onSelect: () => {
                setOpened(id);
              },
            },
          ];

          /* ⚠️ The jump only while the finding is still true — the detail dialog argues why. */
          if (finding.status === 'open') {
            items.push({
              label: goLabel(t, finding.subjectType),
              icon: 'arrow-right',
              onSelect: () => {
                jump(finding);
              },
            });
          }

          if (canManage) {
            items.push(
              finding.exception === null
                ? {
                    label: t('alerts.action.dismiss'),
                    icon: 'square-dashed',
                    onSelect: () => {
                      setDismissingAll(false);
                      setDismissing(id);
                    },
                  }
                : {
                    label: t('alerts.action.restore'),
                    icon: 'undo',
                    onSelect: () => {
                      void write(`/projects/${project.id}/alerts/${id}/dismiss`, {
                        method: 'DELETE',
                      });
                    },
                  },
            );
          }

          return items;
        }}
        /**
         * ⚠️⚠️ **THE FOOTER COUNTS THE PROJECT, NOT THE PAGE OR THE FILTER.** The mockup's own
         * `alFooter` is computed from `alertSummary(pid)` rather than from `rows`, and it has to
         * be: *"8 aktív"* under a table filtered to one area would read as eight in that area.
         */
      />

      <AlertDetailDialog
        finding={openedFinding}
        canManage={canManage}
        onClose={() => {
          setOpened(null);
        }}
        onGo={jump}
        onDismiss={(finding) => {
          setOpened(null);
          setDismissingAll(false);
          setDismissing(finding.id);
        }}
        onRestore={(finding) => {
          setOpened(null);
          void write(`/projects/${project.id}/alerts/${finding.id}/dismiss`, {
            method: 'DELETE',
          }).then((result) => {
            if (result.ok) toast(t('alerts.toast.restored'));
          });
        }}
      />

      <DismissAlertDialog
        finding={dismissingFinding}
        sameKind={
          dismissingFinding === null
            ? 0
            : active.filter((one) => one.kind === dismissingFinding.kind).length
        }
        allOnly={dismissingAll}
        busy={waiving}
        onClose={() => {
          setDismissing(null);
        }}
        onConfirm={(reason, all) => {
          const id = dismissing;
          if (id === null) return;
          setWaiving(true);
          void write(`/projects/${project.id}/alerts/${id}/${all ? 'dismiss-all' : 'dismiss'}`, {
            method: 'POST',
            body: JSON.stringify({ reason }),
          }).then((result) => {
            setWaiving(false);
            if (!result.ok) return;
            setDismissing(null);
            toast(
              all
                ? t('alerts.toast.waivedAll', { count: result.waived ?? 0 })
                : t('alerts.toast.waived'),
            );
          });
        }}
      />
      <Toaster
        toasts={[...toasts]}
        label={t('settings.toasts')}
        dismissLabel={t('common.close')}
        onDismiss={(dismissed) => {
          setToasts((current) => current.filter((one) => one.id !== dismissed));
        }}
      />
    </div>
  );
}

/**
 * 30 Oct 2026 · the row a kind's findings fold into. Severity, name and area are the kind's; the
 * rest sums the members — how many subjects, what they cost RISK together (capped at three per
 * kind, as the score caps it), the newest detection and how many still count.
 */
function groupRowOf(
  t: Translate,
  id: string,
  members: readonly AlertFindingRow[],
  expanded: boolean,
): DataListRow {
  const first = members[0];
  if (first === undefined) return { id, cells: {} };
  const severity = severityOf(first.kind);
  const penalty = penaltyOf(first.kind);
  const counting = members.filter((one) => alertIsActive(one)).length;
  const name = alertName(t, first.kind);
  const newest = members.reduce(
    (latest, one) => (one.firstDetectedAt > latest ? one.firstDetectedAt : latest),
    first.firstDetectedAt,
  );
  const total = penalty * Math.min(counting, 3);

  return {
    id,
    group: { expanded, label: t('alerts.group.toggle', { name, count: members.length }) },
    cells: {
      severity: {
        dot:
          counting > 0
            ? SEVERITY_TONE[severity]
            : `color-mix(in oklch, ${SEVERITY_TONE[severity]} 32%, transparent)`,
        text: severityLabel(t, severity),
        muted: counting === 0,
      },
      name: { text: name, bold: counting > 0, muted: counting === 0, count: members.length },
      subject: { text: t('alerts.group.subjects', { count: members.length }), muted: true },
      area: { badge: { label: areaLabel(t, first.kind), variant: 'outline' } },
      penalty:
        counting > 0
          ? {
              text: t('alerts.penalty.value', { penalty: String(total) }),
              mono: true,
              title: t('alerts.penalty.tip', { penalty: String(total), cap: String(penalty * 3) }),
            }
          : {
              text: t('alerts.penalty.dash'),
              mono: true,
              muted: true,
              title: t('alerts.penalty.none'),
            },
      detected: { text: newest.slice(0, 10), mono: true, muted: true },
      state:
        counting > 0
          ? {
              badge: {
                label: t('alerts.group.active', { count: counting }),
                variant: 'secondary',
              },
            }
          : { text: t('alerts.view.archived'), muted: true },
    },
  };
}

/**
 * ⚠️⚠️ **A DOT PLUS A WORD, NEVER A COLOUR ALONE.** `readme.md`'s rule and the mockup's own cell
 * shape: `{ dot, text }`. Severity read from hue only is unreadable to a tenth of the people who
 * open this tab, and this is a list whose whole job is to be triaged at a glance.
 */
function rowOf(t: Translate, finding: AlertFindingRow): DataListRow {
  const severity = severityOf(finding.kind);
  const penalty = penaltyOf(finding.kind);
  const counting = alertIsActive(finding);
  const waived = finding.exception !== null;

  return {
    id: finding.id,
    cells: {
      severity: {
        /* ⚠️ Muted where it no longer counts — the mockup fades the dot rather than dropping it,
           so an archived row still says how bad the thing WAS. */
        dot: counting
          ? SEVERITY_TONE[severity]
          : `color-mix(in oklch, ${SEVERITY_TONE[severity]} 32%, transparent)`,
        text: severityLabel(t, severity),
        muted: !counting,
      },
      name: { text: alertName(t, finding.kind), bold: counting, muted: !counting },
      subject: { text: finding.subjectLabel, muted: true },
      area: { badge: { label: areaLabel(t, finding.kind), variant: 'outline' } },
      penalty: counting
        ? {
            text: t('alerts.penalty.value', { penalty: String(penalty) }),
            mono: true,
            title: t('alerts.penalty.tip', {
              penalty: String(penalty),
              cap: String(penalty * 3),
            }),
          }
        : {
            text: t('alerts.penalty.dash'),
            mono: true,
            muted: true,
            title: t('alerts.penalty.none'),
          },
      detected: { text: finding.firstDetectedAt.slice(0, 10), mono: true, muted: true },
      /**
       * ⚠️⚠️ **THREE STATES IN THE COLUMN OVER TWO IN THE DATABASE.** *Megoldva* is the data being
       * fixed; *Mellőzve* is somebody deciding the rule does not apply. Both leave the score and
       * the mockup drew one word for them, which would make a waiver indistinguishable from a fix
       * — and a waiver is the one a reviewer needs to find.
       */
      state: waived
        ? {
            icon: 'square-dashed',
            text: t('alerts.state.waived'),
            muted: true,
            title: t('alerts.state.waivedBy', {
              name: finding.exception?.byName ?? '',
              reason: finding.exception?.reason ?? '',
            }),
          }
        : finding.status === 'resolved'
          ? {
              icon: 'circle-check',
              text: t('alerts.state.resolved'),
              muted: true,
              title: t('alerts.state.resolvedOn', {
                date: (finding.resolvedAt ?? '').slice(0, 10),
              }),
            }
          : { badge: { label: t('alerts.state.active'), variant: 'secondary' } },
    },
  };
}
